import {
  FetchState,
  FetchStateKey,
  TransferSchedule,
  DistributionSchedulesState
} from "@/shared/types";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import * as BankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import { defineStore } from "pinia";
import _ from "lodash";
import { isScheduleCancelledOrCompleted } from "@/shared/components/contributionsV3/scheduleStatusHelper";

const pageSize = 10;

function getSchedules (state: DistributionSchedulesState)
  : TransferSchedule[] {
  if (state.distributionSchedules.fetchState !== FetchState.Loaded) {
    return [];
  }

  return state.distributionSchedules.schedules;
}

function paginateSchedules (state: DistributionSchedulesState)
  : TransferSchedule[] {
  return getSchedules(state)
    .sort((a, b) => {
      if (!isScheduleCancelledOrCompleted(a) && isScheduleCancelledOrCompleted(b)) {
        return -1;
      }
      else if (isScheduleCancelledOrCompleted(a) && !isScheduleCancelledOrCompleted(b)) {
        return 1;
      }
      else if (isScheduleCancelledOrCompleted(a) === isScheduleCancelledOrCompleted(b)) {
        return schedulesDateSort(a, b);
      }
      return 0;
    })
    .slice((state.distributionSchedules.pageNumber - 1) * pageSize,
      state.distributionSchedules.pageNumber * pageSize)
    .map(s => {
      return { ...s,
        // to be filled in during a future ticket
        upcomingDates: [],
        nextDate: null
      };
    });
}

function schedulesDateSort (a: TransferSchedule, b: TransferSchedule): number {
  return moment(a.scheduleStartDate).isSame((moment(b.scheduleStartDate))) ? 0
    : moment(a.scheduleStartDate).isAfter(moment(b.scheduleStartDate)) ? -1 : 1;
}

function getPageCount (state: DistributionSchedulesState): number {
  return Math.max(Math.ceil(getSchedules(state).length / pageSize), 1);
}

export const initialState: DistributionSchedulesState = {
  distributionSchedules: {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    pageNumber: 1,
    schedules: null
  }
};

export const useDistributionSchedulesStore = defineStore("distributionSchedules", {
  state: (): DistributionSchedulesState => (_.cloneDeep(initialState)),
  getters: {
    schedulesLoaded: state => state.distributionSchedules.fetchState === FetchState.Loaded,
    schedulesLoading: state => state.distributionSchedules.fetchState === FetchState.Loading,
    schedulesPageCount: state => getPageCount(state),
    schedulesForPage: state => paginateSchedules(state),
    hasSchedules: state => state.distributionSchedules.schedules.length > 0
  },
  actions: {
    invalidateSchedulesFetchState (key: FetchStateKey) {
      if (fetchStateKeyMatch(key, this.distributionSchedules.fetchKey)) {
        return;
      }

      this.distributionSchedules.schedules = null;
      this.distributionSchedules.fetchKey = null;
      this.distributionSchedules.fetchState = FetchState.Uninitialized;
    },
    setSchedulesFetchState ({ fetchState, key }: { fetchState: FetchState, key: FetchStateKey }) {
      this.distributionSchedules.fetchState = fetchState;
      this.distributionSchedules.fetchKey = key;
    },
    setDistributionSchedules (personalContributionSchedules: TransferSchedule[]) {
      this.distributionSchedules.schedules = personalContributionSchedules;
    },
    setSchedulesPage (page: number) {
      this.distributionSchedules.pageNumber = page;
    },
    getDistributionSchedules (args?: { username?: string, partnerId?: number }): Promise<void> {
      args = args || {};
      const fetchKey: FetchStateKey = {
        username: args.username,
        partnerId: args.partnerId
      };

      this.invalidateSchedulesFetchState(fetchKey);
      if (this.distributionSchedules.fetchState !== FetchState.Uninitialized) {
        return Promise.resolve();
      }

      this.setSchedulesFetchState({ fetchState: FetchState.Loading, key: fetchKey });

      return BankService.getDistributionSchedules(args.username, args.partnerId).then(resp => {
        this.setDistributionSchedules(resp.data.map(x => {
          return {
            ...x,
            scheduleStartDate: x.scheduleStartDate.substring(0, 10),
            scheduleEndDate: x.scheduleEndDate && x.scheduleEndDate.substring(0, 10)
          };
        }));
        this.setSchedulesFetchState({ fetchState: FetchState.Loaded, key: fetchKey });
      }).catch(err => {
        storeHelper.handleError({ error: err, rethrow: false });
      });
    }
  }
});